import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/auth/AuthContext";
import { useCart } from "@/cart/CartContext";

export function Layout() {
  const { user, logout } = useAuth();
  const { cart } = useCart();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  const navClass = ({ isActive }: { isActive: boolean }) =>
    `px-3 py-2 rounded-md text-sm font-medium ${
      isActive
        ? "bg-brand-100 text-brand-800"
        : "text-gray-700 hover:bg-gray-100"
    }`;

  return (
    <div className="min-h-screen flex flex-col">
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-16">
          <Link to="/catalog" className="text-xl font-semibold text-brand-700">
            かんたん在庫
          </Link>
          <nav className="hidden md:flex items-center gap-1">
            <NavLink to="/catalog" className={navClass}>
              カタログ
            </NavLink>
            <NavLink to="/cart" className={navClass}>
              カート
              {cart.item_count > 0 && (
                <span className="ml-1 inline-flex items-center justify-center rounded-full bg-brand-600 text-white text-xs w-5 h-5">
                  {cart.item_count}
                </span>
              )}
            </NavLink>
            <NavLink to="/orders" className={navClass}>
              発注履歴
            </NavLink>
            {(user?.tenant?.plan === "premium" || user?.tenant?.is_in_trial) && (
              <NavLink to="/shopee/confirm" className={navClass}>
                Shopee 連携
              </NavLink>
            )}
            <NavLink to="/account" className={navClass}>
              アカウント
            </NavLink>
          </nav>
          <div className="flex items-center gap-3 text-sm">
            {user && (
              <>
                <div className="hidden sm:block text-gray-600">
                  <span className="font-medium">{user.tenant?.name}</span>
                  <span className="ml-2 text-xs uppercase text-brand-700">{user.tenant?.plan}</span>
                </div>
                <button type="button" className="btn-secondary" onClick={handleLogout}>
                  ログアウト
                </button>
              </>
            )}
          </div>
        </div>
      </header>
      <PaymentStatusBanner />
      <TrialBanner />
      <main className="flex-1 max-w-7xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Outlet />
      </main>
      <footer className="py-6 border-t border-gray-200 text-center text-xs text-gray-500">
        © かんたん在庫
      </footer>
    </div>
  );
}

/**
 * サブスク支払い状態バナー。
 * - past_due: 支払い失敗。カード情報の更新を促す。新規発注は API 側でブロック。
 * - cancelled: サブスク解約済み。実質は is_active=false の予定だが念のため表示。
 */
function PaymentStatusBanner() {
  const { user } = useAuth();
  const status = user?.tenant?.payment_status;
  if (!status || status === "active") return null;

  if (status === "past_due") {
    return (
      <div className="bg-red-50 border-b border-red-200 text-red-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 text-sm flex items-start gap-2">
          <span className="text-lg leading-none">⚠️</span>
          <div className="flex-1">
            <strong>お支払いに失敗しています。</strong>
            サブスクリプションのお支払いが確認できないため、新規発注を停止しています。
            カード情報をご確認・更新のうえ、しばらくお待ちください。問題が続く場合は
            お問い合わせください。
          </div>
        </div>
      </div>
    );
  }
  if (status === "cancelled") {
    return (
      <div className="bg-gray-100 border-b border-gray-300 text-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 text-sm flex items-start gap-2">
          <span className="text-lg leading-none">ℹ️</span>
          <div className="flex-1">
            サブスクリプションは解約済みです。再開をご希望の場合はお問い合わせください。
          </div>
        </div>
      </div>
    );
  }
  return null;
}

/**
 * トライアル状態バナー。
 * - トライアル期間中: 残日数 + 「全機能をお試しいただけます」
 * - トライアル終了直後 (30日以内) かつ Premium 以外: アップグレード導線 (Case B)
 */
function TrialBanner() {
  const { user } = useAuth();
  const tenant = user?.tenant;
  if (!tenant) return null;

  const trialEndIso = tenant.trial_end;
  if (!trialEndIso) return null;

  const trialEnd = new Date(trialEndIso);
  if (isNaN(trialEnd.getTime())) return null;

  const now = new Date();
  const msPerDay = 86_400_000;

  // トライアル期間中
  if (tenant.is_in_trial) {
    const remainingDays = Math.max(
      0,
      Math.ceil((trialEnd.getTime() - now.getTime()) / msPerDay),
    );
    return (
      <div className="bg-amber-50 border-b border-amber-200 text-amber-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2 text-sm flex items-center gap-2">
          <span>🎁</span>
          <div className="flex-1">
            <strong>トライアル期間中</strong>：あと{" "}
            <strong>{remainingDays} 日</strong> でトライアル終了。期間中はプランによらず
            <strong>全機能</strong>（全商品の購入・Shopee 連携など）をお試しいただけます。
          </div>
        </div>
      </div>
    );
  }

  // トライアル終了直後 (過去 30 日以内) + Premium 以外 → アップグレード導線
  const daysSinceEnd = (now.getTime() - trialEnd.getTime()) / msPerDay;
  if (
    daysSinceEnd >= 0 &&
    daysSinceEnd <= 30 &&
    tenant.plan !== "premium"
  ) {
    return (
      <div className="bg-blue-50 border-b border-blue-200 text-blue-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2 text-sm flex items-center gap-2">
          <span>ℹ️</span>
          <div className="flex-1">
            <strong>トライアル期間が終了しました。</strong>{" "}
            Shopee 連携などの全機能を継続してご利用いただくには、Premium プランへの
            変更をご検討ください。{" "}
            <Link to="/account" className="underline font-medium">
              プラン変更 ›
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return null;
}
